import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { FunctionComponent, useMemo } from "react";

interface LoadmapProps {
  segments: Array<{
    name: string;
    values: number[];
  }>;
  times: string[];
  trackName: string;
  height?: number;
}

export const Loadmap: FunctionComponent<LoadmapProps> = ({
  segments,
  times,
  trackName: _trackName,
  height = 400,
}) => {
  const seriesData = useMemo(() => {
    const columnSeries = segments.map((segment) => ({
      type: "column" as const,
      name: segment.name,
      data: segment.values,
    }));

    const totalData = times.map((_, idx) => {
      return segments.reduce((sum, segment) => sum + segment.values[idx], 0);
    });

    const totalSeries = {
      type: "line" as const,
      name: "Total",
      data: totalData,
      yAxis: 1,
    };

    return [...columnSeries, totalSeries];
  }, [segments, times]);

  const options: Highcharts.Options = {
    credits: {
      enabled: false,
    },
    chart: {
      styledMode: true,
      height: height,
    },
    title: {
      text: undefined,
    },
    xAxis: {
      categories: times,
    },
    yAxis: [
      {
        min: 0,
        title: {
          text: "Passengers",
        },
      },
      {
        min: 0,
        title: {
          text: "Total",
        },
        opposite: true,
      },
    ],
    legend: {
      align: "center",
      verticalAlign: "bottom",
    },
    tooltip: {
      formatter: function () {
        let s = "<b>" + this.x + "</b><br/>";
        let total = 0;

        this.points?.forEach(function (point) {
          s +=
            '<span style="color:' +
            point.color +
            '">\u25CF</span> ' +
            point.series.name +
            ": " +
            (point.y ?? 0).toLocaleString() +
            "<br/>";
          total += point.y || 0;
        });

        s += "<br/><b>Total: " + total.toLocaleString() + " Passengers</b>";
        return s;
      },
      shared: true,
      useHTML: true,
    },
    plotOptions: {
      column: {
        stacking: "normal",
        dataLabels: {
          enabled: false,
        },
        borderWidth: 0,
        groupPadding: 0.1,
        pointPadding: 0.05,
      },
      line: {
        dataLabels: {
          enabled: false,
        },
      },
    },
    series: seriesData as Highcharts.SeriesOptionsType[],
  };

  return (
    <div className="w-full highcharts-dark">
      <HighchartsReact
        highcharts={Highcharts}
        constructorType="chart"
        options={options}
      />
    </div>
  );
};
